// A member's own accountability log (<product path>/accountability-log; docs/mastermind-admin-plan.md
// 4.2 and 4.4), in the mastermind's own look, under Actions in the menu. It reads only
// hub.my_accountability_log, which gives the reader's own record and takes no person, so nobody can ask
// for anyone else's; while staff view as a member it's that member's, read only (the product route
// draws the read-only line). Printing it is the browser's own print box; the member's own print isn't
// recorded.
//
// The Accelerator critique's members 3, 8, 9 and 13 (20 Oct): while the refocus card leads, the log's own headline,
// what's slipped and the push are kept for print only; an overdue payment's membership line says the day everything
// stays open until, from their access; a missed session's "recording not watched" opens its recording; and on a phone
// Print sits at the bottom, in About this log (Log.jsx). The log is the Accelerator's shared read (useMastermind.js),
// read with the home's rows when this page opens first, and read again alone when they were read before, so the page
// and the home never disagree and the heaviest read isn't made twice. The product's sessions go with it, so an open
// month is reviewed at a session under way when the log was read, not the 1 after (log.js runningSession, 22 Oct).
import { useCallback, useMemo } from 'react'
import * as f from '../../lib/format.js'
import { paidByOther } from '../../billing/payer.js'
import { useProductData } from '../../product/ProductData.jsx'
import { isReleased } from '../../product/model.js'
import { AccountabilityLog, usePrintClass } from '../../products/mastermind/Log.jsx'
import { LOG, LOG_PAGE } from '../../products/mastermind/logWords.js'
import { LoadFailed, Loading, useAccelerator } from '../../products/accelerator/Page.jsx'
import { RefocusCard } from '../../products/accelerator/Cards.jsx'
import { refocusView } from '../../products/accelerator/model.js'
import { loadMyLog } from '../../products/accelerator/data.js'

export { loadMyLog }

export default function MastermindAccountabilityLog({ product, owned = null }) {
  usePrintClass()
  // their log, and their goal for the refocus card (the home's rows, shared)
  const mm = useAccelerator(product, { freshLog: true })
  const { data } = useProductData()
  const access = owned?.access || null
  const overdue = access?.status === 'overdue'
  const by = overdue && access.suspend_on ? f.dayMonthLong(access.suspend_on) : null
  const other = overdue && paidByOther(access)
  const pay = useMemo(() => (overdue ? { by, other } : null), [overdue, by, other])
  // a missed session's recording, once it's open to them
  const recordingHref = useCallback(sessionId => {
    const rec = (data?.recordings || []).find(r => r.session_id === sessionId && r.key && isReleased(data, r.id))
    return rec ? `${product.path || ''}/${rec.key}` : null
  }, [data, product.path])

  if (mm.status === 'loading') return <Loading className="acc-page" />
  if (mm.status === 'error' || !mm.data || mm.data.logFailed) return <LoadFailed onRetry={mm.reload} />
  const doc = mm.data.log
  if (!doc) {
    return (
      <div className="acc-page mlog-page">
        <section className="pl-head">
          <div className="pl-eyebrow">{product.name}</div>
          <h1 className="pl-h1">{LOG.title}</h1>
        </section>
        <p className="mlog-empty-page">{LOG.empty}</p>
      </div>
    )
  }
  const tools = (
    <>
      <button type="button" className="pp-btn mlog-print" onClick={() => window.print()}>{LOG.print}</button>
      <span className="mlog-hint">{LOG_PAGE.printHint}</span>
    </>
  )
  // behind, the refocus card at the top (Adam, 18 Oct, 16:12), on screen only
  const refocus = refocusView(doc, mm.data.goal)
  const lead = refocus ? <div className="mlog-refocus"><RefocusCard view={refocus} /></div> : null
  return (
    <div className="acc-page mlog-page">
      <AccountabilityLog doc={doc} look="product" tools={tools} lead={lead} pay={pay} recordingHref={recordingHref} aboutTools={tools} sessions={data?.sessions || null} />
    </div>
  )
}
